// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../types/glsl.d.ts" />

import assert from '../style-spec/util/assert';
import preludeCommon from './_prelude.glsl';
import preludeFrag from './_prelude.fragment.glsl';
import preludeVert from './_prelude.vertex.glsl';
import backgroundFrag from './background.fragment.glsl';
import backgroundVert from './background.vertex.glsl';
import backgroundPatternFrag from './background_pattern.fragment.glsl';
import backgroundPatternVert from './background_pattern.vertex.glsl';
import circleFrag from './circle.fragment.glsl';
import circleVert from './circle.vertex.glsl';
import clippingMaskFrag from './clipping_mask.fragment.glsl';
import clippingMaskVert from './clipping_mask.vertex.glsl';
import heatmapFrag from './heatmap.fragment.glsl';
import heatmapVert from './heatmap.vertex.glsl';
import heatmapTextureFrag from './heatmap_texture.fragment.glsl';
import heatmapTextureVert from './heatmap_texture.vertex.glsl';
import collisionBoxFrag from './collision_box.fragment.glsl';
import collisionBoxVert from './collision_box.vertex.glsl';
import collisionCircleFrag from './collision_circle.fragment.glsl';
import collisionCircleVert from './collision_circle.vertex.glsl';
import placementDebugFrag from './placement_debug.fragment.glsl';
import placementDebugVert from './placement_debug.vertex.glsl';
import debugFrag from './debug.fragment.glsl';
import debugVert from './debug.vertex.glsl';
import fillFrag from './fill.fragment.glsl';
import fillVert from './fill.vertex.glsl';
import fillOutlineFrag from './fill_outline.fragment.glsl';
import fillOutlineVert from './fill_outline.vertex.glsl';
import fillOutlinePatternFrag from './fill_outline_pattern.fragment.glsl';
import fillOutlinePatternVert from './fill_outline_pattern.vertex.glsl';
import fillPatternFrag from './fill_pattern.fragment.glsl';
import fillPatternVert from './fill_pattern.vertex.glsl';
import lineBlendCompositeFrag from './line_blend_composite.fragment.glsl';
import lineBlendCompositeVert from './line_blend_composite.vertex.glsl';
import lineBlendReduceFrag from './line_blend_reduce.fragment.glsl';
import lineBlendReduceVert from './line_blend_reduce.vertex.glsl';
import fillExtrusionFrag from './fill_extrusion.fragment.glsl';
import fillExtrusionVert from './fill_extrusion.vertex.glsl';
import fillExtrusionPatternFrag from './fill_extrusion_pattern.fragment.glsl';
import fillExtrusionPatternVert from './fill_extrusion_pattern.vertex.glsl';
import hillshadePrepareFrag from './hillshade_prepare.fragment.glsl';
import hillshadePrepareVert from './hillshade_prepare.vertex.glsl';
import hillshadeFrag from './hillshade.fragment.glsl';
import hillshadeVert from './hillshade.vertex.glsl';
import lineFrag from './line.fragment.glsl';
import lineVert from './line.vertex.glsl';
import linePatternFrag from './line_pattern.fragment.glsl';
import linePatternVert from './line_pattern.vertex.glsl';
import rasterFrag from './raster.fragment.glsl';
import rasterVert from './raster.vertex.glsl';
import symbolFrag from './symbol.fragment.glsl';
import symbolVert from './symbol.vertex.glsl';
import skyboxFrag from './skybox.fragment.glsl';
import skyboxGradientFrag from './skybox_gradient.fragment.glsl';
import skyboxVert from './skybox.vertex.glsl';
import preludeTerrainVert from './_prelude_terrain.vertex.glsl';
import preludeFogVert from './_prelude_fog.vertex.glsl';
import preludeFogFrag from './_prelude_fog.fragment.glsl';
import preludeUBOProperties from './_prelude_ubo_properties.glsl';
import preludeLighting from './_prelude_lighting.glsl';
import preludeRasterArrayFrag from './_prelude_raster_array.glsl';
import preludeIndicatorCutoutFrag from './_prelude_indicator_cutout.fragment.glsl';
import skyboxCaptureFrag from './skybox_capture.fragment.glsl';
import skyboxCaptureVert from './skybox_capture.vertex.glsl';
import atmosphereFrag from './atmosphere.fragment.glsl';
import atmosphereVert from './atmosphere.vertex.glsl';
import starsFrag from './stars.fragment.glsl';
import starsVert from './stars.vertex.glsl';
import occlusionFrag from './occlusion.fragment.glsl';
import occlusionVert from './occlusion.vertex.glsl';
// 3d-style related shaders
import preludeShadowVert from '../../3d-style/shaders/_prelude_shadow.vertex.glsl';
import preludeShadowFrag from '../../3d-style/shaders/_prelude_shadow.fragment.glsl';
import preludeMaterialTableVert from './_prelude_material_table.vertex.glsl';

import type {ShaderSource} from '../render/program';
import type {DynamicDefinesType} from '../render/program/program_uniforms';

const INCLUDE_REGEX = /^#include\s+"([^"]+)"\s*\r?\n/gm;
const PRAGMA_REGEX = /#pragma mapbox: ([\w\-]+) ([\w]+) ([\w]+) ([\w]+)/g;

const IDENTIFIER_REGEX = /\b[A-Za-z_][A-Za-z0-9_]*\b/g;
const PREPROCESSOR_KEYWORDS = new Set(['ifdef', 'ifndef', 'elif', 'if', 'defined']);

// Feature cutout is gl-native only. Shared GLSL #includes this prelude, but gl-js never
// enables FEATURE_CUTOUT, so a comment stub is enough for include resolution and define tracking.
const preludeFeatureCutoutFrag = '// feature cutout (gl-native only)\n';
const preludeFeatureCutoutVert = '// feature cutout (gl-native only)\n';

const commonDefines: Set<DynamicDefinesType> = new Set();
parseUsedPreprocessorDefines(preludeCommon, commonDefines);
parseUsedPreprocessorDefines(preludeVert, commonDefines);
parseUsedPreprocessorDefines(preludeFrag, commonDefines);

export const includeMap: Record<string, string> = {
    '_prelude_fog.vertex.glsl': preludeFogVert,
    '_prelude_terrain.vertex.glsl': preludeTerrainVert,
    '_prelude_shadow.vertex.glsl': preludeShadowVert,
    '_prelude_material_table.vertex.glsl': preludeMaterialTableVert,
    '_prelude_fog.fragment.glsl': preludeFogFrag,
    '_prelude_shadow.fragment.glsl': preludeShadowFrag,
    '_prelude_lighting.glsl': preludeLighting,
    '_prelude_ubo_properties.glsl': preludeUBOProperties,
    '_prelude_raster_array.glsl': preludeRasterArrayFrag,
    '_prelude_indicator_cutout.fragment.glsl': preludeIndicatorCutoutFrag,
    '_prelude_feature_cutout.fragment.glsl': preludeFeatureCutoutFrag,
    '_prelude_feature_cutout.vertex.glsl': preludeFeatureCutoutVert
};

// Populated during precompilation
const defineMap: Record<string, Set<DynamicDefinesType>> = {};
export const preludeCommonSource = preludeCommon;

const preludeVertPrecisionQualifiers = `precision highp float;`;
const preludeFragPrecisionQualifiers = `precision mediump float;`;

const preludeFragExtensions = `
#if defined(GL_EXT_blend_func_extended) && defined(DUAL_SOURCE_BLENDING)
#extension GL_EXT_blend_func_extended : require
#endif`;

export const preludeShaders = {
    preludeTerrain: compile('', preludeTerrainVert),
    preludeUBOProperties: compile('', preludeUBOProperties),
    preludeFog: compile(preludeFogFrag, preludeFogVert),
    preludeShadow: compile(preludeShadowFrag, preludeShadowVert),
    preludeRasterArray: compile(preludeRasterArrayFrag, ''),
    preludeLighting: compile(preludeLighting, preludeLighting),
    preludePrecisionQualifiers: compile(preludeFragPrecisionQualifiers, preludeVertPrecisionQualifiers),
    prelude: compile(preludeFrag, preludeVert),
    preludeExtensions: compile(preludeFragExtensions, ''),
} as const;

export const FRAGMENT_PRELUDE_BLOCK = [
    preludeFragExtensions,
    preludeFragPrecisionQualifiers,
    preludeCommon,
    preludeShaders.prelude.fragmentSource
].join('\n');

export const VERTEX_PRELUDE_BLOCK = [
    preludeVertPrecisionQualifiers,
    preludeCommon,
    preludeShaders.prelude.vertexSource
].join('\n');

export default {
    background: compile(backgroundFrag, backgroundVert),
    backgroundPattern: compile(backgroundPatternFrag, backgroundPatternVert),
    circle: compile(circleFrag, circleVert),
    clippingMask: compile(clippingMaskFrag, clippingMaskVert),
    heatmap: compile(heatmapFrag, heatmapVert),
    heatmapTexture: compile(heatmapTextureFrag, heatmapTextureVert),
    collisionBox: compile(collisionBoxFrag, collisionBoxVert),
    collisionCircle: compile(collisionCircleFrag, collisionCircleVert),
    placementDebug: compile(placementDebugFrag, placementDebugVert),
    debug: compile(debugFrag, debugVert),
    fill: compile(fillFrag, fillVert),
    fillOutline: compile(fillOutlineFrag, fillOutlineVert),
    fillOutlinePattern: compile(fillOutlinePatternFrag, fillOutlinePatternVert),
    fillPattern: compile(fillPatternFrag, fillPatternVert),
    lineBlendComposite: compile(lineBlendCompositeFrag, lineBlendCompositeVert),
    lineBlendReduce: compile(lineBlendReduceFrag, lineBlendReduceVert),
    fillExtrusion: compile(fillExtrusionFrag, fillExtrusionVert),
    fillExtrusionPattern: compile(fillExtrusionPatternFrag, fillExtrusionPatternVert),
    hillshadePrepare: compile(hillshadePrepareFrag, hillshadePrepareVert),
    hillshade: compile(hillshadeFrag, hillshadeVert),
    line: compile(lineFrag, lineVert),
    linePattern: compile(linePatternFrag, linePatternVert),
    raster: compile(rasterFrag, rasterVert),
    symbol: compile(symbolFrag, symbolVert),
    skybox: compile(skyboxFrag, skyboxVert),
    skyboxGradient: compile(skyboxGradientFrag, skyboxVert),
    skyboxCapture: compile(skyboxCaptureFrag, skyboxCaptureVert),
    globeAtmosphere: compile(atmosphereFrag, atmosphereVert),
    stars: compile(starsFrag, starsVert),
    occlusion: compile(occlusionFrag, occlusionVert)
} as const;

export function parseUsedPreprocessorDefines(source: string, defines: Set<DynamicDefinesType>): void {
    const lines = source.split('\n');
    for (let line of lines) {
        line = line.trimStart();
        if (line[0] !== '#') continue;

        if (!line.includes('if')) continue;
        if (line.startsWith('#endif')) continue;

        const matches = line.match(IDENTIFIER_REGEX);
        if (!matches) continue;

        for (const match of matches) {
            if (!PREPROCESSOR_KEYWORDS.has(match)) {
                defines.add(match as DynamicDefinesType);
            }
        }
    }
}

function isIntegerType(type: string): boolean {
    const intTypes = new Set(['uint', 'int', 'uvec2', 'ivec2', 'uvec3', 'ivec3', 'uvec4', 'ivec4']);
    return intTypes.has(type);
}

// Expand #pragmas to #ifdefs.
export function compile(fragmentSource: string, vertexSource: string): ShaderSource {
    const fragmentPragmas: Set<string> = new Set();
    const vertexIncludes: string[] = [];
    const fragmentIncludes: string[] = [];

    fragmentSource = fragmentSource.replace(INCLUDE_REGEX, (_, name: string) => {
        fragmentIncludes.push(name);
        return '';
    });

    vertexSource = vertexSource.replace(INCLUDE_REGEX, (_, name: string) => {
        vertexIncludes.push(name);
        return '';
    });

    assert(!vertexSource.includes('flat out'), 'The usage of "flat" qualifier is disallowed, see: https://bugs.webkit.org/show_bug.cgi?id=268071');

    let usedDefines: Set<DynamicDefinesType> = new Set(commonDefines);
    parseUsedPreprocessorDefines(fragmentSource, usedDefines);
    parseUsedPreprocessorDefines(vertexSource, usedDefines);

    for (const includePath of [...vertexIncludes, ...fragmentIncludes]) {
        // Check for *registration*, not truthiness: a prelude can legitimately be empty. The
        // build's dead-branch elimination (build/glsl_dead_code.js) reduces preludes whose entire
        // body is guarded by a gl-native-only define to the empty string — e.g.
        // `_prelude_material_table.vertex.glsl`, which is wholly inside
        // `#ifdef HAS_SHADER_STORAGE_BLOCK_material_buffer` (WebGL 2 has no SSBOs, and
        // `_prelude.vertex.glsl` supplies the `#ifndef` fallback macros). A truthiness check
        // reports those as "Unknown include" in dev builds, where asserts are not stripped.
        assert(includeMap[includePath] !== undefined, `Unknown include: ${includePath}`);

        if (!defineMap[includePath]) {
            defineMap[includePath] = new Set();
            parseUsedPreprocessorDefines(includeMap[includePath], defineMap[includePath]);
        }

        usedDefines = new Set([...usedDefines, ...defineMap[includePath]]);
    }

    fragmentSource = fragmentSource.replace(PRAGMA_REGEX, (_, operation, precision, type: string, name: string) => {
        fragmentPragmas.add(name);
        if (operation === 'define') {
            const interpolation = isIntegerType(type) ? 'flat ' : '';
            return `
#ifndef HAS_UNIFORM_u_${name}
${interpolation}in ${precision} ${type} ${name};
#else
uniform ${precision} ${type} u_${name};
#endif
`;
        } else if (operation === 'initialize') {
            return `
#ifdef HAS_UNIFORM_u_${name}
    ${precision} ${type} ${name} = u_${name};
#endif
`;
        } else if (operation === 'define-attribute') {
            return `
#ifdef HAS_ATTRIBUTE_a_${name}
    in ${precision} ${type} ${name};
#endif
`;
        } else if (operation === 'initialize-attribute') {
            return '';
        }
    });

    vertexSource = vertexSource.replace(PRAGMA_REGEX, (_, operation, precision, type: string, name: string) => {

        const materialOffsetNameDefineName = `MATERIAL_ATTRIBUTE_OFFSET_${name}`;
        const attrType = type === 'float' ? 'vec2' : type;
        const materialAttribExpression = `GET_ATTRIBUTE_${attrType}(a_${name}, materialInfo, ${materialOffsetNameDefineName})`;
        const unpackType = name.includes('color') ? 'color' : attrType;

        if (operation === 'define-attribute-vertex-shader-only') {
            return `
#ifdef HAS_ATTRIBUTE_a_${name}
in ${precision} ${type} a_${name};
#endif
`;
        } else if (fragmentPragmas.has(name)) {
            if (operation === 'define') {
                const interpolation = isIntegerType(type) ? 'flat ' : '';
                return `
#ifndef HAS_UNIFORM_u_${name}
uniform lowp float u_${name}_t;
    #if !defined(${materialOffsetNameDefineName})
        in ${precision} ${attrType} a_${name};
    #endif
${interpolation}out ${precision} ${type} ${name};
#else
uniform ${precision} ${type} u_${name};
#endif
`;
            } else if (operation === 'initialize') {
                if (unpackType === 'vec4' || unpackType === 'uvec4') {
                    // vec4 attributes are only used for cross-faded properties, and are not packed
                    return `
#ifndef HAS_UNIFORM_u_${name}
    ${name} = a_${name};
#else
    ${precision} ${type} ${name} = u_${name};
#endif
`;
                } else {
                    return `
#if !defined(HAS_UNIFORM_u_${name})
    #ifdef ${materialOffsetNameDefineName}
        ${name} = unpack_mix_${unpackType}(${materialAttribExpression}, u_${name}_t);
    #else
        ${name} = unpack_mix_${unpackType}(a_${name}, u_${name}_t);
    #endif
#else
    ${precision} ${type} ${name} = u_${name};
#endif
`;
                }
            } else if (operation === 'define-attribute') {
                return `
#ifdef HAS_ATTRIBUTE_a_${name}
    in ${precision} ${type} a_${name};
    out ${precision} ${type} ${name};
#endif
`;
            } else if (operation === 'initialize-attribute') {
                return `
#ifdef HAS_ATTRIBUTE_a_${name}
    ${name} = a_${name};
#endif
`;
            }
        } else {
            if (operation === 'define') {
                return `
#ifndef HAS_UNIFORM_u_${name}
uniform lowp float u_${name}_t;
    #if !defined(${materialOffsetNameDefineName})
        in ${precision} ${attrType} a_${name};
    #endif
#else
uniform ${precision} ${type} u_${name};
#endif
`;
            } else if (operation === 'define-instanced') {
                if (unpackType === 'mat4') {
                    return `
#ifdef INSTANCED_ARRAYS
in vec4 a_${name}0;
in vec4 a_${name}1;
in vec4 a_${name}2;
in vec4 a_${name}3;
#else
uniform ${precision} ${type} u_${name};
#endif
`;
                } else {
                    return `
#ifdef INSTANCED_ARRAYS
in ${precision} ${attrType} a_${name};
#else
uniform ${precision} ${type} u_${name};
#endif
`;
                }
            } else if (operation === 'initialize-attribute-custom') {
                return `
#ifdef HAS_ATTRIBUTE_a_${name}
    ${precision} ${type} ${name} = a_${name};
#endif
`;
            } else /* if (operation === 'initialize') */ {
                if (unpackType === 'vec4' || unpackType === 'uvec4') {
                    // vec4 attributes are only used for cross-faded properties, and are not packed
                    return `
#ifndef HAS_UNIFORM_u_${name}
    #ifdef ${materialOffsetNameDefineName}
        ${precision} ${type} ${name} = ${materialAttribExpression};
    #else
        ${precision} ${type} ${name} = a_${name};
    #endif
#else
    ${precision} ${type} ${name} = u_${name};
#endif
`;
                } else /* */ {
                    return `
#ifndef HAS_UNIFORM_u_${name}
    #ifdef ${materialOffsetNameDefineName}
        ${precision} ${type} ${name} = unpack_mix_${unpackType}(${materialAttribExpression}, u_${name}_t);
    #else
        ${precision} ${type} ${name} = unpack_mix_${unpackType}(a_${name}, u_${name}_t);
    #endif
#else
    ${precision} ${type} ${name} = u_${name};
#endif
`;
                }
            }
        }
    });

    return {fragmentSource, vertexSource, usedDefines, vertexIncludes, fragmentIncludes};
}
